import React, { useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  Box, Button, makeStyles, Typography,
} from '@material-ui/core';
import { useIsFirstRender } from 'utils/hooks/useIsFirstRender';
import leftNav from '../icons/left-nav.svg';
import rightNav from '../icons/right-nav.svg';

const YEAR_NAVIGATION = {
  MIN: 1960,
  MAX: 1984,
  RANGE: 24,
};
const YEAR_DOM_SELECTOR = '.MuiPickersYear-root';

const useStyles = makeStyles(() => ({
  root: {
    minHeight: 20,
    padding: 20,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'white',
  },
  button: {
    minWidth: 'unset',
  },
  icon: {
    width: 6,
    height: 12,
  },
}));

const calculateInitialState = (initialFocusedDate, minDate, maxDate) => {
  const initialDateYear = moment(initialFocusedDate).year();
  const minDateYear = moment(minDate).year();
  const maxDateYear = moment(maxDate).year();

  const result = {
    startDate: +moment().year(YEAR_NAVIGATION.MIN).format('YYYY'),
    endDate: +moment().year(YEAR_NAVIGATION.MAX).format('YYYY'),
  };

  if (initialDateYear <= minDateYear) {
    result.startDate = minDateYear;
    if (maxDateYear > (minDateYear + YEAR_NAVIGATION.RANGE)) {
      result.endDate = initialDateYear + YEAR_NAVIGATION.RANGE;
    } else {
      result.endDate = maxDateYear;
    }
  } else if (initialDateYear >= maxDateYear) {
    if (minDateYear < (maxDateYear - YEAR_NAVIGATION.RANGE)) {
      result.startDate = initialDateYear - YEAR_NAVIGATION.RANGE;
    } else {
      result.startDate = minDateYear;
    }
    result.endDate = maxDateYear;
  }

  return result;
};

const scrollToNode = (findTargetNode) => {
  const findNode = Array.from(document.querySelectorAll(YEAR_DOM_SELECTOR)).find(findTargetNode);
  if (findNode) {
    findNode.scrollIntoView({ behavior: 'smooth' });
  }
};

/**
 * props are coming from MUI 4 datepicker ToolbarComponent https://material-ui-pickers.dev/api/KeyboardDatePicker
*/
export const DatePickerToolbar = ({
  minDate, maxDate, initialFocusedDate,
}) => {
  const isFirstRender = useIsFirstRender();
  const initState = isFirstRender
    ? calculateInitialState(initialFocusedDate, minDate, maxDate)
    : {};
  const [startDate, setStartDate] = useState(initState.startDate);
  const [endDate, setEndDate] = useState(initState.endDate);
  const classes = useStyles();
  const newStartDate = startDate - YEAR_NAVIGATION.RANGE;
  const newEndDate = endDate + YEAR_NAVIGATION.RANGE;

  const handlePrevYears = () => {
    if (startDate < (moment(minDate).year() + YEAR_NAVIGATION.RANGE)) {
      setStartDate(moment(minDate).year());
      setEndDate(moment(minDate).year() + YEAR_NAVIGATION.RANGE);
      scrollToNode((node) => +node.textContent === moment(minDate).year());
      return;
    }
    setStartDate(newStartDate);
    setEndDate(startDate);
    scrollToNode((node) => +node.textContent === newStartDate);
  };

  const handleNextYears = () => {
    if (endDate > (moment(maxDate).year() - YEAR_NAVIGATION.RANGE)) {
      setStartDate(moment(maxDate).year() - YEAR_NAVIGATION.RANGE);
      setEndDate(moment(maxDate).year());
      scrollToNode((node) => +node.textContent === moment(maxDate).year());
      return;
    }
    setStartDate(endDate);
    setEndDate(newEndDate);
    scrollToNode((node) => +node.textContent === endDate);
  };

  return (
    <Box className={classes.root}>
      <Button className={classes.button} onClick={handlePrevYears}>
        <img className={classes.icon} src={leftNav} alt="left-nav" />
      </Button>
      <Typography>
        {startDate}
        {' '}
        -
        {' '}
        {endDate}
      </Typography>
      <Button className={classes.button} onClick={handleNextYears}>
        <img className={classes.icon} src={rightNav} alt="right-nav" />
      </Button>
    </Box>
  );
};

DatePickerToolbar.propTypes = {
  minDate: PropTypes.object.isRequired,
  maxDate: PropTypes.object.isRequired,
  initialFocusedDate: PropTypes.object.isRequired,
};
